import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import type { InputRef } from 'antd';
import { Button, Flex, Input, Spin, Typography } from 'antd';

import Modal from '/ui/Modal';

const { Text } = Typography;

type Props = {
  visible: boolean;
  onClose: () => void;
  onGenerate: (fileName: string) => Promise<void>;
};

const GenerateNeuroModal = ({ visible, onClose, onGenerate }: Props) => {
  const intl = useIntl();
  const inputRef = useRef<InputRef>(null);
  const [fileName, setFileName] = useState('');
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!visible) {
      setFileName('');
      setError(null);
      return;
    }

    window.setTimeout(() => inputRef.current?.focus(), 0);
  }, [visible]);

  const handleGenerate = useCallback(async () => {
    const trimmedName = fileName.trim();

    if (generating) return;

    if (!trimmedName || trimmedName.length > 255) {
      setError(
        intl.formatMessage({ id: 'files.atlasGenerateModal.errorName' })
      );
      return;
    }

    setGenerating(true);
    setError(null);

    try {
      await onGenerate(trimmedName);
      onClose();
    } catch (err) {
      console.error(err);
      setError(
        intl.formatMessage({ id: 'files.atlasGenerateModal.errorGenerate' })
      );
    } finally {
      setGenerating(false);
    }
  }, [fileName, generating, intl, onClose, onGenerate]);

  return (
    <Modal
      className="files-page__atlas-generate-modal"
      open={visible}
      onCancel={onClose}
      footer={null}
      centered
      destroyOnHidden
      width={500}
      mask={{
        closable: !generating,
      }}
    >
      <Spin spinning={generating}>
        <Flex className="files-page__atlas-generate-inner" vertical gap={16}>
          <Text className="files-page__atlas-generate-title">
            <FormattedMessage id="files.generateUnitedModal.title" />
          </Text>
          <Input
            ref={inputRef}
            value={fileName}
            maxLength={255}
            disabled={generating}
            placeholder={intl.formatMessage({
              id: 'files.atlasGenerateModal.namePlaceholder',
            })}
            onChange={(event) => setFileName(event.target.value)}
            onPressEnter={() => void handleGenerate()}
          />
          {error && <Text type="danger">{error}</Text>}
          <Button
            type="primary"
            className="files-page__atlas-generate-btn"
            loading={generating}
            disabled={generating}
            onClick={() => void handleGenerate()}
          >
            <FormattedMessage id="files.atlasGenerateModal.generate" />
          </Button>
        </Flex>
      </Spin>
    </Modal>
  );
};

export default GenerateNeuroModal;
